import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

export function CheckoutSuccessPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying'); // verifying, paid, pending, failed
  const [message, setMessage] = useState('');
  const [plan, setPlan] = useState('');
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); setMessage('Invalid checkout session'); return; }

    const verify = async () => {
      try {
        const { data } = await api.get(`/api/subscriptions/verify/${sessionId}`);
        if (data.status === 'paid') {
          setStatus('paid');
          setPlan(data.plan || '');
          setMessage(data.message);
        } else if (retries < 5) {
          setTimeout(() => setRetries(r => r + 1), 3000);
        } else {
          setStatus('pending');
          setMessage('Payment is still being processed. You will receive a notification once confirmed.');
        }
      } catch (err) {
        setStatus('failed');
        setMessage(err.response?.data?.detail || 'Verification failed');
      }
    };

    verify();
  }, [sessionId, retries, api]);

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4" data-testid="checkout-success-page">
      <Card className="bg-[#041A3B] border-white/10 max-w-md w-full">
        <CardContent className="p-8 text-center space-y-6">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-[#00E5FF] mx-auto" />
              <h2 className="font-outfit text-2xl text-white font-semibold">Verifying Payment...</h2>
              <p className="text-[#A0B4CC]">Please wait while we confirm your payment.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle className="w-16 h-16 text-[#00E676] mx-auto" />
              <h2 className="font-outfit text-2xl text-white font-semibold">Subscription Activated!</h2>
              {plan && <p className="text-[#00E5FF] font-medium text-lg">{plan}</p>}
              <p className="text-[#A0B4CC]">{message}</p>
              <Button onClick={() => navigate('/dashboard')} className="btn-primary w-full" data-testid="go-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-[#FFEA00] mx-auto" />
              <h2 className="font-outfit text-2xl text-white font-semibold">Payment Processing</h2>
              <p className="text-[#A0B4CC]">{message}</p>
              <Button onClick={() => navigate('/dashboard')} className="btn-primary w-full">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-[#FF1744] mx-auto" />
              <h2 className="font-outfit text-2xl text-white font-semibold">Payment Failed</h2>
              <p className="text-[#A0B4CC]">{message}</p>
              <Button onClick={() => navigate('/pricing')} className="btn-primary w-full">
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
